import { Highlights } from '@/components/modules/MarketList/UserMarket';
import { PortalPositionRow } from '@/components/portal-modules/PortalPositionTable';
import { GroupBy } from '@/components/trader-modules/Blotters/PositionGroupSwitch';
import { getCurrency } from '@/utils/hooks/useCurrency';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import { GetColorIndexesReturn, useInstrument } from '@/utils/hooks/useInstrument';
import usePreviousState from '@/utils/hooks/usePreviousState';
import { translatePair } from '@/utils/symbolMapping';
import { CellContext } from '@tanstack/react-table';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import { MdChevronRight, MdInfoOutline } from 'react-icons/md';
import { generalAmountCell } from './currencyHelper';

interface PortalPositionColumnsParams {
    groupBy: GroupBy;
    formatQty: Intl.NumberFormat;
    formatDate: Intl.DateTimeFormat;
    baseCurrency: string;
    setInstrumentInfoContext: any;
    handleOpenContextMenu: any;
}

const shouldShowComponent = (groupBy: GroupBy, depth: number, children: any) => {
    if (!(groupBy === 'Products' && depth === 0)) return children;
    return undefined;
};

const shouldSemiBold = (groupBy: GroupBy, depth: number) => {
    return (groupBy === 'Products' && depth <= 1) || (groupBy === 'None' && depth === 0) ? 'font-semibold' : '';
};

export const portalPositionColumns = (params: PortalPositionColumnsParams) => {
    const { groupBy, baseCurrency, formatQty, formatDate, setInstrumentInfoContext, handleOpenContextMenu } = params;

    const columns = [
        {
            size: 180,
            accessorKey: 'instrumentName',
            header: ({ table }) => {
                return (
                    <div className="flex space-x-1 items-center truncate">
                        <button
                            onClick={(e) => {
                                table.toggleAllRowsExpanded();
                                e.stopPropagation();
                            }}
                            title={table.getIsAllRowsExpanded() ? 'Collapse all rows' : 'Expand all rows'}
                            className="relative transform scale-125 left-0">
                            <MdChevronRight
                                className={cn('h-3.5 w-3.5 font-bold text-green duration-200', {
                                    ['rotate-90 ']: table.getIsAllRowsExpanded()
                                })}
                                aria-hidden
                            />
                        </button>

                        <span className="truncate">Symbol</span>
                    </div>
                );
            },
            cell: ({ row, getValue }: CellContext<PortalPositionRow, any>) => {
                const value = getValue();
                const show = translatePair(value);
                return (
                    <div
                        className="flex items-center gap-1"
                        style={{
                            paddingLeft: `${row.getCanExpand() ? row.depth * 1 : row.depth * 1.5}rem`
                        }}>
                        <div className={`flex space-x-1`}>
                            {row.getCanExpand() && (
                                <button title={row.getIsExpanded() ? 'Collapse current row' : 'Expand current row'}>
                                    <MdChevronRight
                                        className={cn('h-3.5 w-3.5 text-green duration-200', {
                                            ['rotate-90']: row.getIsExpanded()
                                        })}
                                        aria-hidden
                                    />
                                </button>
                            )}

                            {groupBy === 'Products' && row.depth === 0 && value}
                            {groupBy === 'Products' && row.depth === 1 && show}
                            {groupBy === 'Products' &&
                                row.depth > 1 &&
                                `VD: ${formatDate.format(new Date(row.original.instrumentName || ''))}`}

                            {groupBy === 'None' && row.depth === 0 && show}
                            {groupBy === 'None' &&
                                row.depth > 0 &&
                                `VD: ${formatDate.format(new Date(row.original.instrumentName || ''))}`}
                        </div>
                        {row.getCanExpand() && !(groupBy === 'Products' && row.depth === 0) && (
                            <div
                                title="Instrument Info"
                                className={cn(
                                    'h-full flex items-center p-1 cursor-pointer hover:text-brand-green-light-light',
                                    {}
                                )}
                                onDrag={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const initialInstrumentName =
                                        row.original.initialInstrumentName || row.original.instrumentName;

                                    const market = {
                                        celer: initialInstrumentName,
                                        show: translatePair(initialInstrumentName),
                                        netdania: initialInstrumentName
                                    };

                                    setInstrumentInfoContext(market);
                                    handleOpenContextMenu(e);
                                }}>
                                <MdInfoOutline className="w-3 h-3" />
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'type',
            header: 'Type',
            cell: ({ row, getValue }: CellContext<PortalPositionRow, any>) =>
                shouldShowComponent(groupBy, row.depth, getValue())
        },
        {
            accessorKey: 'amount',
            header: 'Amount',
            cell: ({ row, getValue }: CellContext<PortalPositionRow, any>) => {
                const amount = row.original.amount;
                const ccy = (row.original.initialInstrumentName || row.original.instrumentName).split('/')[0];

                return shouldShowComponent(
                    groupBy,
                    row.depth,
                    generalAmountCell({
                        amount,
                        ccy,
                        formatQty,
                        className: shouldSemiBold(groupBy, row.depth)
                    })
                );
            }
        },
        {
            accessorKey: 'currentPrice',
            header: 'Current Price',
            cell: ({ row, getValue }: CellContext<PortalPositionRow, any>) => {
                const value = getValue();

                const prevMid = usePreviousState(value);
                const { formatPip, formatHighlights } = useInstrument(
                    row.original.initialInstrumentName || row.original.instrumentName
                );
                const formattedMid = useMemo(() => value && formatPip(value), [value]);
                const [midHighlights, setMidHighlights] = useState<GetColorIndexesReturn | undefined>();

                useDidUpdate(() => {
                    if (value && prevMid) setMidHighlights(formatHighlights(prevMid, value, midHighlights));
                }, [value]);

                return shouldShowComponent(
                    groupBy,
                    row.depth,
                    <div
                    // className={cn('', shouldSemiBold(groupBy, row.depth))}
                    >
                        {isNaN(value) || row.original.amount === 0 ? (
                            '-'
                        ) : (
                            <Highlights ticker={formattedMid} highlights={midHighlights} />
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'marketValueInBaseCurrency',
            header: `Market Value ${baseCurrency ? `(${baseCurrency})` : ''}`,
            cell: ({ row, getValue }: CellContext<PortalPositionRow, any>) => {
                const value = getValue();
                const { show, order_decimals } = getCurrency(baseCurrency);

                const number = new Intl.NumberFormat(formatQty.resolvedOptions().locale, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: Math.max(0, order_decimals - 2)
                });

                return generalAmountCell({
                    amount: value,
                    ccy: show,
                    formatQty: number,
                    className: shouldSemiBold(groupBy, row.depth)
                });
            }
        },
        {
            accessorKey: 'totalProfitLoss',
            header: 'Unrealised P/L',
            width: 150,
            cell: ({ row, getValue }: CellContext<PortalPositionRow, any>) => {
                const value = getValue();
                const ccy2 = (row.original.initialInstrumentName || row.original.instrumentName).split('/')[1];
                const { show, order_decimals } = getCurrency(ccy2);

                const number = new Intl.NumberFormat(formatQty.resolvedOptions().locale, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: Math.max(0, order_decimals - 2)
                });

                return shouldShowComponent(
                    groupBy,
                    row.depth,
                    generalAmountCell({
                        amount: value,
                        ccy: show,
                        formatQty: number,
                        className: shouldSemiBold(groupBy, row.depth)
                    })
                );
            }
        },
        {
            accessorKey: 'totalProfitLossInBaseCurrency',
            header: `Unrealised P/L ${baseCurrency ? `(${baseCurrency})` : ''}`,
            width: 150,
            cell: ({ row, getValue }: CellContext<PortalPositionRow, any>) => {
                const value = getValue();
                const { show, order_decimals } = getCurrency(baseCurrency);

                const number = new Intl.NumberFormat(formatQty.resolvedOptions().locale, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: Math.max(0, order_decimals - 2)
                });

                return generalAmountCell({
                    amount: value,
                    ccy: show,
                    formatQty: number,
                    className: shouldSemiBold(groupBy, row.depth)
                });
            }
        }
    ];
    return groupBy === 'None' ? columns : columns.filter((column) => column.accessorKey !== 'type');
};
